import type {
  ProjectTask,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  SupplierOrg,
  TimberProject,
  TimberSupplierStructureRef,
  VendorCapability,
  VendorWorkType,
} from '../types';
import type { GroupTimelineFamily } from './groupTimeline';
import { getSubtaskStructures } from './subtaskStructures';
import {
  approvedSubmissionIdsForReleaseSelection,
  approvedSupplierOrgIdsForReleaseSelection,
  filterPackagesForReleaseSelection,
  PRE_PORTAL_RELEASE_ID,
} from './timberRfqPrePortalVersion';
import { inviteHasStructureScope, normalizeName } from './timberRfqStructureScope';
import {
  isTimberQuoteFullScope,
  normalizeQuotedStructures,
} from './timberQuoteStreamKey';
import { selectRfqProjectForDeal } from './vendorAdminProjectSummary';
import { orgHasCapability } from './vendorHelpers';

export const UNASSIGNED_SUPPLIER_KEY = '__unassigned__';
export const MULTIPLE_SUPPLIER_KEY = '__multiple__';
export const SUPPLIER_GROUP_ID_PREFIX = '__supplier__:';
export const SUPPLIER_TASK_ID_SEP = '::__sup::';

export const UNASSIGNED_SUPPLIER_LABEL = 'Unassigned';
export const MULTIPLE_SUPPLIER_LABEL = 'Multiple';

export function supplierDisplayTaskId(taskId: string, orgKey: string): string {
  return `${taskId}${SUPPLIER_TASK_ID_SEP}${orgKey}`;
}

export function parseSupplierDisplayTaskId(
  id: string | null | undefined,
): { taskId: string; orgKey: string } | null {
  if (!id) return null;
  const i = id.indexOf(SUPPLIER_TASK_ID_SEP);
  if (i <= 0) return null;
  const taskId = id.slice(0, i);
  const orgKey = id.slice(i + SUPPLIER_TASK_ID_SEP.length);
  if (!taskId || !orgKey) return null;
  return { taskId, orgKey };
}

export function unwrapSupplierDisplayTaskId(id: string | undefined): string | undefined {
  if (!id) return id;
  return parseSupplierDisplayTaskId(id)?.taskId ?? id;
}

export type GroupTimelineSupplierSource = 'manual' | 'quote';

export interface GroupTimelineSupplierResolution {
  orgIds: string[];
  primaryOrgId: string | null;
  source: GroupTimelineSupplierSource;
}

export interface GroupTimelineSupplierIndex {
  byTaskId: Map<string, GroupTimelineSupplierResolution>;
  /** Quote-only match (ignores {@link ProjectTask.supplierOrgIds}) for the task-panel hint. */
  quoteByTaskId: Map<string, GroupTimelineSupplierResolution>;
  orgNameById: Map<string, string>;
}

export function workTypeForGroupTimelineFamily(family: GroupTimelineFamily): VendorWorkType | null {
  if (family === 'production') return 'cnc_quote';
  if (family === 'procurement') return 'timber_rfq';
  return null;
}

export function supplierGroupId(orgKey: string): string {
  return `${SUPPLIER_GROUP_ID_PREFIX}${orgKey}`;
}

export function parseSupplierGroupId(groupId: string | undefined): string | null {
  if (!groupId?.startsWith(SUPPLIER_GROUP_ID_PREFIX)) return null;
  return groupId.slice(SUPPLIER_GROUP_ID_PREFIX.length);
}

export function orgNameFromMap(orgId: string | null | undefined, names: Map<string, string>): string {
  if (!orgId) return UNASSIGNED_SUPPLIER_LABEL;
  if (orgId === UNASSIGNED_SUPPLIER_KEY) return UNASSIGNED_SUPPLIER_LABEL;
  if (orgId === MULTIPLE_SUPPLIER_KEY) return MULTIPLE_SUPPLIER_LABEL;
  return (names.get(orgId) || orgId).trim() || orgId;
}

function emptyResolution(): GroupTimelineSupplierResolution {
  return { orgIds: [], primaryOrgId: null, source: 'quote' };
}

function pickPrimaryOrgId(orgIds: string[], names: Map<string, string>): string | null {
  if (orgIds.length === 0) return null;
  const sorted = [...orgIds].sort((a, b) =>
    orgNameFromMap(a, names).localeCompare(orgNameFromMap(b, names)),
  );
  return sorted[0] ?? null;
}

function dealIdForProject(
  projectId: string,
  projects: TimberProject[],
): string | null {
  const p = projects.find((x) => x.id === projectId);
  return p?.dealId?.trim() || null;
}

export function taskMatchesSupplierStructureScope(
  task: ProjectTask,
  refs: TimberSupplierStructureRef[] | undefined,
  scoped: boolean,
): boolean {
  if (!scoped) return true;
  const taskStructs = getSubtaskStructures(task);
  if (!taskStructs.length || !refs?.length) return false;
  const ids = new Set(refs.map((s) => s.id).filter(Boolean));
  const names = new Set(refs.map((s) => normalizeName(s.name)).filter(Boolean));
  return taskStructs.some((s) => ids.has(s.id) || names.has(normalizeName(s.name)));
}

function scopeForOrg(
  orgId: string,
  rfqProject: RfqProject,
  packages: RfqPackage[],
): { scoped: boolean; refs: TimberSupplierStructureRef[] } {
  const invite = (rfqProject.timberSupplierInvites ?? []).find((i) => i.supplierOrgId === orgId);
  if (invite) {
    if (inviteHasStructureScope(invite)) {
      return { scoped: true, refs: invite.structures ?? [] };
    }
    return { scoped: false, refs: [] };
  }
  const orgPkgs = packages
    .filter((p) => p.rfqProjectId === rfqProject.id && p.supplierOrgId === orgId)
    .slice()
    .sort((a, b) => {
      const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
      const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
      return tb - ta;
    });
  const latest = orgPkgs[0];
  const refs = latest?.scopeStructures ?? latest?.cncPackagePayload?.structures;
  if (refs?.length) return { scoped: true, refs };
  return { scoped: false, refs: [] };
}

function latestReleaseIdWithAwards(
  project: RfqProject,
  packages: RfqPackage[],
  kind: 'cnc' | 'timber',
): string | null {
  const candidates = new Set<string>();
  if (kind === 'cnc') {
    for (const [k, ids] of Object.entries(project.approvedSupplierOrgIdsByRelease ?? {})) {
      if (ids?.length) candidates.add(k);
    }
    for (const [k, id] of Object.entries(project.approvedSupplierOrgIdByRelease ?? {})) {
      if (id) candidates.add(k);
    }
    if (project.approvedSupplierOrgId) candidates.add(PRE_PORTAL_RELEASE_ID);
  } else {
    for (const [k, ids] of Object.entries(project.approvedSubmissionIdsByRelease ?? {})) {
      if (ids?.length) candidates.add(k);
    }
    if (project.approvedSubmissionIds?.length) candidates.add(PRE_PORTAL_RELEASE_ID);
  }
  if (candidates.size === 0) return null;

  const projectPackages = packages.filter((p) => p.rfqProjectId === project.id);
  const scored = Array.from(candidates).map((id) => {
    const pkgs = filterPackagesForReleaseSelection(projectPackages, id);
    const maxVer = Math.max(0, ...pkgs.map((p) => p.releaseVersionIndex ?? 0));
    const maxCreated = Math.max(
      0,
      ...pkgs.map((p) => (p.createdAt instanceof Date ? p.createdAt.getTime() : 0)),
    );
    return { id, maxVer, maxCreated };
  });
  scored.sort((a, b) => b.maxVer - a.maxVer || b.maxCreated - a.maxCreated);
  return scored[0]?.id ?? Array.from(candidates)[0] ?? null;
}

type ApprovedQuoteStructureScope = {
  orgId: string;
  refs: TimberSupplierStructureRef[];
  scoped: boolean;
};

function structureRefsForApprovedSubmission(
  sub: RfqSubmission,
  rfqProject: RfqProject,
  packages: RfqPackage[],
): { refs: TimberSupplierStructureRef[]; scoped: boolean } {
  if (!isTimberQuoteFullScope(sub)) {
    return { refs: normalizeQuotedStructures(sub.quotedStructures), scoped: true };
  }
  const pkg =
    (sub.rfqPackageId ? packages.find((p) => p.id === sub.rfqPackageId) : undefined)
    ?? packages
      .filter((p) => p.rfqProjectId === rfqProject.id && p.supplierOrgId === sub.supplierOrgId)
      .slice()
      .sort((a, b) => {
        const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return tb - ta;
      })[0];
  const pkgScope = normalizeQuotedStructures(pkg?.scopeStructures);
  if (pkgScope.length) return { refs: pkgScope, scoped: true };
  return scopeForOrg(sub.supplierOrgId, rfqProject, packages);
}

function approvedQuoteStructureScopesForFamily(
  family: GroupTimelineFamily,
  rfqProject: RfqProject | null,
  packages: RfqPackage[],
  submissions: RfqSubmission[],
): ApprovedQuoteStructureScope[] {
  if (!rfqProject?.id) return [];
  const workType = workTypeForGroupTimelineFamily(family);
  if (!workType) return [];

  const projectPackages = packages.filter((p) => p.rfqProjectId === rfqProject.id);
  const byPkg = new Map<string, RfqSubmission[]>();
  for (const sub of submissions) {
    if (!sub.rfqPackageId) continue;
    const arr = byPkg.get(sub.rfqPackageId) ?? [];
    arr.push(sub);
    byPkg.set(sub.rfqPackageId, arr);
  }

  const scopes: ApprovedQuoteStructureScope[] = [];
  const seenSubmissionIds = new Set<string>();

  if (workType === 'timber_rfq') {
    const releaseId = latestReleaseIdWithAwards(rfqProject, packages, 'timber');
    let approvedIds = approvedSubmissionIdsForReleaseSelection(
      rfqProject,
      projectPackages,
      byPkg,
      releaseId,
    );
    if (!approvedIds.length && rfqProject.approvedSubmissionIds?.length) {
      approvedIds = rfqProject.approvedSubmissionIds;
    }
    for (const subId of approvedIds) {
      const sub = submissions.find((s) => s.id === subId);
      if (!sub?.supplierOrgId || !sub.id || seenSubmissionIds.has(sub.id)) continue;
      seenSubmissionIds.add(sub.id);
      const { refs, scoped } = structureRefsForApprovedSubmission(sub, rfqProject, packages);
      scopes.push({ orgId: sub.supplierOrgId, refs, scoped });
    }
    return scopes;
  }

  const releaseId = latestReleaseIdWithAwards(rfqProject, packages, 'cnc');
  const approvedSubIds = approvedSubmissionIdsForReleaseSelection(
    rfqProject,
    projectPackages,
    byPkg,
    releaseId,
  );
  if (!approvedSubIds.length && rfqProject.approvedSubmissionIds?.length) {
    for (const id of rfqProject.approvedSubmissionIds) if (id) approvedSubIds.push(id);
  }
  for (const subId of approvedSubIds) {
    const sub = submissions.find((s) => s.id === subId);
    if (!sub?.supplierOrgId || !sub.id || seenSubmissionIds.has(sub.id)) continue;
    seenSubmissionIds.add(sub.id);
    const { refs, scoped } = structureRefsForApprovedSubmission(sub, rfqProject, packages);
    scopes.push({ orgId: sub.supplierOrgId, refs, scoped });
  }

  const orgsWithSubs = new Set(scopes.map((s) => s.orgId));
  const orgIds = awardedOrgIdsForFamily(family, rfqProject, packages, submissions);
  for (const orgId of orgIds) {
    if (orgsWithSubs.has(orgId)) continue;
    const scope = scopeForOrg(orgId, rfqProject, packages);
    scopes.push({ orgId, refs: scope.refs, scoped: scope.scoped });
  }
  return scopes;
}

function awardedOrgIdsForFamily(
  family: GroupTimelineFamily,
  rfqProject: RfqProject | null,
  packages: RfqPackage[],
  submissions: RfqSubmission[],
): string[] {
  if (!rfqProject) return [];
  const workType = workTypeForGroupTimelineFamily(family);
  if (!workType) return [];

  if (workType === 'cnc_quote') {
    const releaseId = latestReleaseIdWithAwards(rfqProject, packages, 'cnc');
    const ids = approvedSupplierOrgIdsForReleaseSelection(rfqProject, releaseId);
    return Array.from(new Set(ids.filter(Boolean)));
  }

  const projectPackages = packages.filter((p) => p.rfqProjectId === rfqProject.id);
  const byPkg = new Map<string, RfqSubmission[]>();
  for (const sub of submissions) {
    if (!sub.rfqPackageId) continue;
    const arr = byPkg.get(sub.rfqPackageId) ?? [];
    arr.push(sub);
    byPkg.set(sub.rfqPackageId, arr);
  }
  const releaseId = latestReleaseIdWithAwards(rfqProject, packages, 'timber');
  let approvedIds = approvedSubmissionIdsForReleaseSelection(
    rfqProject,
    projectPackages,
    byPkg,
    releaseId,
  );
  if (!approvedIds.length && rfqProject.approvedSubmissionIds?.length) {
    approvedIds = rfqProject.approvedSubmissionIds;
  }
  const orgIds: string[] = [];
  for (const subId of approvedIds) {
    const sub = submissions.find((s) => s.id === subId);
    const orgId = sub?.supplierOrgId;
    if (orgId && !orgIds.includes(orgId)) orgIds.push(orgId);
  }
  return orgIds;
}

export function invitedSupplierOptionsForDeal(params: {
  dealId: string | null | undefined;
  rfqProjects: RfqProject[];
  supplierOrgs: SupplierOrg[];
}): Array<{ id: string; name: string }> {
  const dealId = (params.dealId || '').trim();
  if (!dealId) return [];
  const ids = new Set<string>();
  for (const workType of ['cnc_quote', 'timber_rfq'] as VendorWorkType[]) {
    const rfq = selectRfqProjectForDeal(dealId, workType, params.rfqProjects);
    if (!rfq) continue;
    if (rfq.supplierOrgId) ids.add(rfq.supplierOrgId);
    for (const id of rfq.supplierOrgIds ?? []) if (id) ids.add(id);
    for (const invite of rfq.timberSupplierInvites ?? []) {
      if (invite.supplierOrgId) ids.add(invite.supplierOrgId);
    }
  }
  const names = new Map(
    params.supplierOrgs.filter((o) => o.id).map((o) => [o.id!, o.name.trim() || o.id!] as const),
  );
  return Array.from(ids)
    .map((id) => ({ id, name: names.get(id) || id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function orgOption(org: SupplierOrg): { id: string; name: string } | null {
  if (!org.id) return null;
  return { id: org.id, name: (org.name || '').trim() || org.id };
}

/**
 * Invited deal orgs, plus the roster for Cut Timber Package (CNC) /
 * Timber Lead Time (timber). Capability filters both invites and the roster.
 */
export function supplierOptionsForTaskPanel(params: {
  dealId: string | null | undefined;
  rfqProjects: RfqProject[];
  supplierOrgs: SupplierOrg[];
  capability?: VendorCapability | null;
  extraOrgIds?: string[];
  /** Include the full roster matching `capability` (or every org if none). */
  includeAllOrgs?: boolean;
}): Array<{ id: string; name: string }> {
  const byId = new Map<string, { id: string; name: string }>();
  const orgById = new Map(params.supplierOrgs.filter((o) => o.id).map((o) => [o.id!, o]));
  const matchesCap = (org: SupplierOrg | undefined): boolean => {
    if (!params.capability) return true;
    return !!org && orgHasCapability(org, params.capability);
  };

  for (const opt of invitedSupplierOptionsForDeal(params)) {
    if (matchesCap(orgById.get(opt.id))) byId.set(opt.id, opt);
  }

  if (params.capability || params.includeAllOrgs) {
    for (const org of params.supplierOrgs) {
      if (!org.id || !matchesCap(org)) continue;
      const opt = orgOption(org);
      if (opt) byId.set(opt.id, opt);
    }
  }

  for (const id of params.extraOrgIds ?? []) {
    if (!id || byId.has(id)) continue;
    const org = orgById.get(id);
    const opt = org ? orgOption(org) : { id, name: id };
    if (opt) byId.set(opt.id, opt);
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function buildGroupTimelineSupplierIndex(params: {
  family: GroupTimelineFamily;
  projects: TimberProject[];
  tasks: ProjectTask[];
  rfqProjects: RfqProject[];
  rfqPackages: RfqPackage[];
  rfqSubmissions: RfqSubmission[];
  supplierOrgs: SupplierOrg[];
}): GroupTimelineSupplierIndex {
  const orgNameById = new Map(
    params.supplierOrgs.filter((o) => o.id).map((o) => [o.id!, (o.name || '').trim() || o.id!] as const),
  );

  const workType = workTypeForGroupTimelineFamily(params.family);
  const awardedByDeal = new Map<string, { scopes: ApprovedQuoteStructureScope[]; rfq: RfqProject }>();
  if (workType) {
    const seenDeals = new Set<string>();
    for (const p of params.projects) {
      const dealId = p.dealId?.trim();
      if (!dealId || seenDeals.has(dealId)) continue;
      seenDeals.add(dealId);
      const rfq = selectRfqProjectForDeal(dealId, workType, params.rfqProjects);
      if (!rfq) continue;
      const scopes = approvedQuoteStructureScopesForFamily(
        params.family,
        rfq,
        params.rfqPackages,
        params.rfqSubmissions,
      );
      awardedByDeal.set(dealId, { scopes, rfq });
    }
  }

  const byTaskId = new Map<string, GroupTimelineSupplierResolution>();
  const quoteByTaskId = new Map<string, GroupTimelineSupplierResolution>();

  const quoteResolve = (task: ProjectTask): GroupTimelineSupplierResolution => {
    const dealId = dealIdForProject(task.projectId, params.projects);
    if (!dealId || !workType) return emptyResolution();
    const awarded = awardedByDeal.get(dealId);
    if (!awarded?.scopes.length) return emptyResolution();
    const matched = Array.from(
      new Set(
        awarded.scopes
          .filter((scope) => taskMatchesSupplierStructureScope(task, scope.refs, scope.scoped))
          .map((scope) => scope.orgId),
      ),
    );
    return {
      orgIds: matched,
      primaryOrgId: pickPrimaryOrgId(matched, orgNameById),
      source: 'quote',
    };
  };

  for (const task of params.tasks) {
    if (!task.id) continue;
    const quoted = quoteResolve(task);
    quoteByTaskId.set(task.id, quoted);
    const manualIds = (task.supplierOrgIds ?? []).filter(Boolean);
    if (manualIds.length > 0 && task.supplierManual !== false) {
      byTaskId.set(task.id, {
        orgIds: manualIds,
        primaryOrgId: pickPrimaryOrgId(manualIds, orgNameById),
        source: 'manual',
      });
      continue;
    }
    byTaskId.set(task.id, quoted);
  }

  const childrenByParent = new Map<string, ProjectTask[]>();
  for (const task of params.tasks) {
    if (!task.parentTaskId) continue;
    const arr = childrenByParent.get(task.parentTaskId) ?? [];
    arr.push(task);
    childrenByParent.set(task.parentTaskId, arr);
  }

  for (const task of params.tasks) {
    if (!task.id || task.parentTaskId) continue;
    const current = byTaskId.get(task.id);
    if (!current || current.source === 'manual' || current.orgIds.length > 0) continue;
    if (getSubtaskStructures(task).length > 0) continue;
    const kids = childrenByParent.get(task.id) ?? [];
    const union = new Set<string>();
    let anyQuote = false;
    for (const kid of kids) {
      const res = kid.id ? byTaskId.get(kid.id) : undefined;
      if (!res) continue;
      if (res.source === 'quote') anyQuote = true;
      for (const id of res.orgIds) union.add(id);
    }
    if (union.size === 0) continue;
    const orgIds = Array.from(union);
    const inherited: GroupTimelineSupplierResolution = {
      orgIds,
      primaryOrgId: pickPrimaryOrgId(orgIds, orgNameById),
      source: anyQuote ? 'quote' : 'manual',
    };
    byTaskId.set(task.id, inherited);
    if (inherited.source === 'quote') quoteByTaskId.set(task.id, inherited);
  }

  return { byTaskId, quoteByTaskId, orgNameById };
}

export function quoteDerivedSupplierLabel(
  task: ProjectTask,
  index: GroupTimelineSupplierIndex,
): string | null {
  if (!task.id) return null;
  const quoted = index.quoteByTaskId.get(task.id);
  if (quoted?.orgIds.length) {
    return quoted.orgIds.map((id) => orgNameFromMap(id, index.orgNameById)).join(', ');
  }
  return null;
}

/**
 * True when this org's timber or CNC package scope excludes a structure job.
 * No invite/package, or an unscoped award, is not a miss.
 */
function viewerStructureScopeMissesTask(
  viewerOrgId: string,
  task: ProjectTask,
  projects: TimberProject[],
  rfqProjects: RfqProject[],
  rfqPackages: RfqPackage[],
): boolean {
  if (!getSubtaskStructures(task).length) return false;
  const dealId = dealIdForProject(task.projectId, projects);
  if (!dealId) return false;

  let sawScope = false;
  let covered = false;
  for (const workType of ['cnc_quote', 'timber_rfq'] as VendorWorkType[]) {
    const rfq = selectRfqProjectForDeal(dealId, workType, rfqProjects);
    if (!rfq?.id) continue;
    const onInvite = (rfq.timberSupplierInvites ?? []).some((i) => i.supplierOrgId === viewerOrgId);
    const onPkg = rfqPackages.some(
      (p) => p.rfqProjectId === rfq.id && p.supplierOrgId === viewerOrgId,
    );
    if (!onInvite && !onPkg) continue;
    sawScope = true;
    const scope = scopeForOrg(viewerOrgId, rfq, rfqPackages);
    if (!scope.scoped || taskMatchesSupplierStructureScope(task, scope.refs, scope.scoped)) {
      covered = true;
    }
  }
  return sawScope && !covered;
}

function viewerOwnsSupplierTask(
  task: ProjectTask,
  viewerOrgId: string,
  resolution: GroupTimelineSupplierResolution | undefined,
  projects: TimberProject[],
  rfqProjects: RfqProject[],
  rfqPackages: RfqPackage[],
): boolean {
  const manual = (task.supplierOrgIds ?? []).filter(Boolean);
  if (manual.length > 0 && task.supplierManual !== false) {
    return manual.includes(viewerOrgId);
  }
  const resolved = (resolution?.orgIds ?? []).filter(Boolean);
  if (resolved.length > 0) return resolved.includes(viewerOrgId);
  if (viewerStructureScopeMissesTask(viewerOrgId, task, projects, rfqProjects, rfqPackages)) {
    return false;
  }
  return true;
}

/**
 * Supplier timelines only show production rows this org is on.
 * Tasks assigned to other mills (manual supplier, quote match, or a structure
 * scope that excludes this org) are dropped. Unassigned shop checklist rows stay.
 * A parent stays when any child stays, or when the parent itself is assigned here.
 */
export function filterTasksVisibleToSupplierOrg(params: {
  tasks: ProjectTask[];
  byTaskId: Map<string, GroupTimelineSupplierResolution>;
  viewerOrgId: string;
  projects: TimberProject[];
  rfqProjects: RfqProject[];
  rfqPackages: RfqPackage[];
}): ProjectTask[] {
  const viewerOrgId = params.viewerOrgId.trim();
  if (!viewerOrgId) return params.tasks;

  const owns = (task: ProjectTask) =>
    viewerOwnsSupplierTask(
      task,
      viewerOrgId,
      task.id ? params.byTaskId.get(task.id) : undefined,
      params.projects,
      params.rfqProjects,
      params.rfqPackages,
    );

  const childrenByParent = new Map<string, ProjectTask[]>();
  for (const task of params.tasks) {
    if (!task.parentTaskId) continue;
    const list = childrenByParent.get(task.parentTaskId) ?? [];
    list.push(task);
    childrenByParent.set(task.parentTaskId, list);
  }

  const ownedChildIds = new Set<string>();
  for (const task of params.tasks) {
    if (!task.parentTaskId || !task.id || !owns(task)) continue;
    ownedChildIds.add(task.id);
  }

  return params.tasks.filter((task) => {
    if (!task.id) return true;
    if (task.parentTaskId) return owns(task);
    const kids = childrenByParent.get(task.id) ?? [];
    if (kids.some((kid) => kid.id && ownedChildIds.has(kid.id))) return true;
    if (!kids.length) return owns(task);
    const manual = (task.supplierOrgIds ?? []).filter(Boolean);
    return manual.length > 0 && task.supplierManual !== false && manual.includes(viewerOrgId);
  });
}

export function primarySupplierForTasks(
  tasks: ProjectTask[],
  byTaskId: Map<string, GroupTimelineSupplierResolution>,
): string | null {
  const ids = new Set<string>();
  for (const t of tasks) {
    if (!t.id || t.parentTaskId) continue;
    const res = byTaskId.get(t.id);
    if (res?.primaryOrgId) ids.add(res.primaryOrgId);
  }
  if (ids.size === 0) return null;
  return Array.from(ids).sort()[0] ?? null;
}
